import { Link } from "react-router-dom";
import { MapPin, Star, Users, Wifi, Shield } from "lucide-react";
import { cldOptimize } from "../lib/api";

const GENDER_COLOR = {
  male: "bg-blue-50 text-blue-700 border-blue-200",
  female: "bg-rose-50 text-rose-700 border-rose-200",
  "co-ed": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function PropertyCard({ p }) {
  const img = cldOptimize(p.images?.[0] || "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200", "w_800");
  const totalBeds = (p.rooms || []).reduce((s, r) => s + (r.available_beds ?? r.beds ?? 0), 0);
  const isSoldOut = p.rooms?.length > 0 && totalBeds === 0;
  const hasWifi = (p.amenities || []).some(a => /wifi/i.test(a));
  const isVerified = p.status === "active";

  return (
    <Link
      to={`/properties/${p.id}`}
      className="group block bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-emerald-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-200"
      data-testid={`property-card-${p.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={img}
          alt={p.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {/* Top badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {p.featured && (
            <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-amber-300 bg-gradient-to-r from-amber-300 to-yellow-400 text-amber-900 shadow-md" data-testid="featured-sticker">
              ⭐ Featured
            </span>
          )}
          <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${GENDER_COLOR[p.gender] || "bg-gray-50 text-gray-700 border-gray-200"} bg-white/95 backdrop-blur`}>
            {p.gender}
          </span>
          {isVerified && (
            <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50/95 text-emerald-700 backdrop-blur">
              <Shield size={11}/> Verified
            </span>
          )}
        </div>
        {/* Beds badge top-right */}
        <div className="absolute top-3 right-3">
          {isSoldOut ? (
            <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-rose-600 text-white" data-testid={`sold-out-${p.id}`}>Sold Out</span>
          ) : totalBeds > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-emerald-700 border border-emerald-200" data-testid={`beds-left-${p.id}`}>
              <Users size={11}/> {totalBeds} bed{totalBeds > 1 ? "s" : ""} left
            </span>
          ) : null}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 md:p-5">
        <h3 className="font-display font-black text-lg leading-tight text-[#0E1F18] line-clamp-2 group-hover:text-emerald-700 transition">
          {p.title}
        </h3>

        <p className="mt-1.5 text-sm text-gray-500 flex items-center gap-1 line-clamp-1">
          <MapPin size={13} className="flex-shrink-0"/> {p.address}
        </p>

        {/* Quick chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {hasWifi && <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full"><Wifi size={11}/> Wifi</span>}
          {p.food_included && <span className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">Mess</span>}
          {p.ac && <span className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full">AC</span>}
          {p.college_nearby && <span className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full truncate max-w-[140px]">{p.college_nearby}</span>}
        </div>

        {/* Price + rating */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">starts from</div>
            <div className="font-display font-black text-2xl text-[#0E1F18] leading-none mt-0.5">
              ₹{p.price_per_month?.toLocaleString()}
              <span className="text-xs font-bold text-gray-500 ml-0.5">/mo</span>
            </div>
          </div>
          {p.avg_rating ? (
            <div className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full">
              <Star size={12} fill="currentColor"/>
              <span className="text-xs font-bold">{p.avg_rating}</span>
            </div>
          ) : (
            <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">New</span>
          )}
        </div>
      </div>
    </Link>
  );
}
